import { useMemo } from 'react';
import _get from 'lodash/get';
import { DetailedProviderContent } from '@kyruus/provider-components';
import { MessageDescriptor } from '@kyruus/intl';

import {
  ProviderMatchProviderV9,
  PMCConfig,
  ProviderMatchProviderLocationV9
} from '@kyruus/types';
import { PROFILE_SECTION_IDS } from '../constants';
import {
  showAboutSection,
  showClinicalInterestsSection,
  showExpertiseSection,
  showInsuranceSection,
  showReviewSection,
  showCareTeamSection,
  showAltProvidersSection,
  showAppointmentsSection,
  getNavItemAriaLabelledBy
} from '../utils';
import messages from '../messages';

interface UseProfileSectionProps {
  config: PMCConfig;
  provider: ProviderMatchProviderV9;
  locations: ProviderMatchProviderLocationV9[];
  careTeamProviders: DetailedProviderContent[];
  altProviders: DetailedProviderContent[];
  shouldRenderHighlightsNav: boolean;
  renderableSections: string[];
}

interface NavItem {
  id: string;
  name: string;
  label: MessageDescriptor | string;
  ariaLabelledBy: string;
  rendered: boolean;
  href: string;
}

interface UseProfileSectionReturnType {
  navItems: NavItem[];
  rendered: {
    highlightsSection: boolean;
    aboutSection: boolean;
    appointmentsSection: boolean;
    videoSection: boolean;
    locationsSection: boolean;
    clinicalInterestsSection: boolean;
    expertiseSection: boolean;
    insuranceSection: boolean;
    reviewsSection: boolean;
    careTeamSection: boolean;
    altProvidersSection: boolean;
  };
}
/**
 * hook that manages:
 * which sections should be rendered on the profile
 * which navigation items should be rendered
 */
export const useProfileSections = ({
  config,
  provider,
  locations,
  careTeamProviders,
  altProviders,
  shouldRenderHighlightsNav,
  renderableSections
}: UseProfileSectionProps): UseProfileSectionReturnType => {
  const rendered = useMemo(() => {
    const highlightsSection = Boolean(shouldRenderHighlightsNav);
    const videoSection = Boolean(provider.video_url);
    const locationsSection = Boolean(locations?.length);
    const aboutSection = showAboutSection(config, provider);
    const appointmentsSection = showAppointmentsSection(config, provider);
    const clinicalInterestsSection = showClinicalInterestsSection(provider);
    const expertiseSection = showExpertiseSection(provider);
    const insuranceSection = showInsuranceSection(config, provider);
    const reviewsSection = showReviewSection(provider);
    const careTeamSection = showCareTeamSection(config, careTeamProviders);
    const altProvidersSection = showAltProvidersSection(config, altProviders);

    return {
      highlightsSection,
      aboutSection,
      appointmentsSection,
      videoSection,
      locationsSection,
      clinicalInterestsSection,
      expertiseSection,
      insuranceSection,
      reviewsSection,
      careTeamSection,
      altProvidersSection
    };
  }, [
    config,
    provider,
    locations,
    altProviders,
    careTeamProviders,
    shouldRenderHighlightsNav
  ]);

  const navItems = useMemo(() => {
    const getNavItem = (sectionId: string): NavItem | null => {
      switch (sectionId) {
        case PROFILE_SECTION_IDS.HIGHLIGHTS:
          return {
            id: PROFILE_SECTION_IDS.HIGHLIGHTS,
            href: `#${PROFILE_SECTION_IDS.HIGHLIGHTS}`,
            name: 'Highlights',
            label: messages.highlights,
            ariaLabelledBy: getNavItemAriaLabelledBy(
              PROFILE_SECTION_IDS.HIGHLIGHTS
            ),
            rendered: rendered.highlightsSection
          };
        case PROFILE_SECTION_IDS.ABOUT:
          return {
            id: PROFILE_SECTION_IDS.ABOUT,
            name: 'About',
            href: `#${PROFILE_SECTION_IDS.ABOUT}`,
            label: messages.aboutNavTitle,
            ariaLabelledBy: getNavItemAriaLabelledBy(PROFILE_SECTION_IDS.ABOUT),
            rendered: rendered.aboutSection
          };
        case PROFILE_SECTION_IDS.APPOINTMENTS:
          return {
            id: PROFILE_SECTION_IDS.APPOINTMENTS,
            href: `#${PROFILE_SECTION_IDS.APPOINTMENTS}`,
            name: 'Appointments',
            label: messages.appointmentsNavTitle,
            ariaLabelledBy: getNavItemAriaLabelledBy(
              PROFILE_SECTION_IDS.APPOINTMENTS
            ),
            rendered: rendered.appointmentsSection
          };
        case PROFILE_SECTION_IDS.VIDEOS:
          return {
            id: PROFILE_SECTION_IDS.VIDEOS,
            href: `#${PROFILE_SECTION_IDS.VIDEOS}`,
            name: 'Videos',
            label: messages.videosNavTitle,
            ariaLabelledBy: getNavItemAriaLabelledBy(
              PROFILE_SECTION_IDS.VIDEOS
            ),
            rendered: rendered.videoSection
          };
        case PROFILE_SECTION_IDS.LOCATIONS:
          return {
            id: PROFILE_SECTION_IDS.LOCATIONS,
            href: `#${PROFILE_SECTION_IDS.LOCATIONS}`,
            name: 'Locations',
            label: messages.locationsNavTitle,
            ariaLabelledBy: getNavItemAriaLabelledBy(
              PROFILE_SECTION_IDS.LOCATIONS
            ),
            rendered: rendered.locationsSection
          };
        case PROFILE_SECTION_IDS.CLINICAL_INTERESTS:
          return {
            id: PROFILE_SECTION_IDS.CLINICAL_INTERESTS,
            href: `#${PROFILE_SECTION_IDS.CLINICAL_INTERESTS}`,
            name: 'Clinical Interests',
            label: messages.clinicalInterestsNavTitle,
            ariaLabelledBy: getNavItemAriaLabelledBy(
              PROFILE_SECTION_IDS.CLINICAL_INTERESTS
            ),
            rendered: rendered.clinicalInterestsSection
          };
        case PROFILE_SECTION_IDS.EXPERTISE:
          return {
            id: PROFILE_SECTION_IDS.EXPERTISE,
            name: 'Expertise',
            href: `#${PROFILE_SECTION_IDS.EXPERTISE}`,
            label: messages.expertiseNavTitle,
            ariaLabelledBy: getNavItemAriaLabelledBy(
              PROFILE_SECTION_IDS.EXPERTISE
            ),
            rendered: rendered.expertiseSection
          };
        case PROFILE_SECTION_IDS.INSURANCE_ACCEPTED:
          return {
            id: PROFILE_SECTION_IDS.INSURANCE_ACCEPTED,
            href: `#${PROFILE_SECTION_IDS.INSURANCE_ACCEPTED}`,
            name: 'Insurance',
            label: messages.insuranceNavTitle,
            ariaLabelledBy: getNavItemAriaLabelledBy(
              PROFILE_SECTION_IDS.INSURANCE_ACCEPTED
            ),
            rendered: rendered.insuranceSection
          };
        case PROFILE_SECTION_IDS.REVIEWS:
          return {
            id: PROFILE_SECTION_IDS.REVIEWS,
            href: `#${PROFILE_SECTION_IDS.REVIEWS}`,
            name: 'Reviews',
            label: messages.reviewsNavTitle,
            ariaLabelledBy: getNavItemAriaLabelledBy(
              PROFILE_SECTION_IDS.REVIEWS
            ),
            rendered: rendered.reviewsSection
          };
        case PROFILE_SECTION_IDS.CARE_TEAM:
          return {
            id: PROFILE_SECTION_IDS.CARE_TEAM,
            href: `#${PROFILE_SECTION_IDS.CARE_TEAM}`,
            name: 'Care Team',
            label: messages.careTeamNavTitle,
            ariaLabelledBy: getNavItemAriaLabelledBy(
              PROFILE_SECTION_IDS.CARE_TEAM
            ),
            rendered: rendered.careTeamSection
          };
        case PROFILE_SECTION_IDS.SIMILAR_PROVIDERS:
          return {
            id: PROFILE_SECTION_IDS.SIMILAR_PROVIDERS,
            name: 'Similar Providers',
            href: `#${PROFILE_SECTION_IDS.SIMILAR_PROVIDERS}`,
            label: messages.altProvidersNavTitle,
            ariaLabelledBy: getNavItemAriaLabelledBy(
              PROFILE_SECTION_IDS.SIMILAR_PROVIDERS
            ),
            rendered: rendered.altProvidersSection
          };
        default: {
          // must be a custom xp field
          const customXPFieldConfig =
            config.provider_v9?.sections?.custom?.find(
              (xpFieldConfig) => xpFieldConfig.id === sectionId
            );
          if (customXPFieldConfig) {
            // if we have an XP field config, check to see if the section actually rendered (has provider data)
            const rendered = Boolean(
              _get(provider, customXPFieldConfig.field, null)
            );
            if (rendered) {
              return {
                id: customXPFieldConfig.id,
                name: customXPFieldConfig.title,
                href: `#${customXPFieldConfig.id}`,
                label: customXPFieldConfig.title,
                ariaLabelledBy: getNavItemAriaLabelledBy(
                  customXPFieldConfig.id
                ),
                rendered: rendered
              };
            }
          }
        }
      }
      return null;
    };
    return renderableSections
      .map((sectionId) => getNavItem(sectionId))
      .filter(Boolean) as NavItem[];
  }, [
    renderableSections,
    rendered,
    config.provider_v9?.sections?.custom,
    provider
  ]);

  return {
    navItems,
    rendered
  };
};
